import { Loader2 } from 'lucide-react';
import { SortableHeader, type SortState } from '../ui/SortableHeader';
import { ColumnResizeHandle } from '../ui/ColumnResizeHandle';
import { useResizableColumns } from '../../hooks/useResizableColumns';
import { formatDate } from '../../utils/format';
import { formatCredited, formatCount } from '../../utils/creditNoteFormat';
import { CreditNoteFlags } from './CreditNoteFlags';
import { CreditNoteLinks } from './CreditNoteLinks';
import {
    creditNoteKey, reasonLabel,
    type CreditNoteDocument, type CreditNoteReasonCatalogEntry, type CreditNoteVocabularies,
} from '../../types/creditNotes';

/**
 * The credit-note list.
 *
 * House table standard, no exceptions: sticky header, sortable, resizable — `SortableHeader`,
 * `ColumnResizeHandle` and `useResizableColumns`, the same three as everywhere else.
 *
 * THE REASON COLUMN SHOWS A STUB AND SAYS SO. dbi and bin carry `GU_Category_BIG`, which the
 * ERP stores TRUNCATED TO 20 CHARACTERS — `8 H CLAIM SCHADENERS` is the whole stored value, not
 * an ellipsis this page added. No table of readable long texts exists in the database yet (open
 * question 1, with Stefan), so the value is rendered in the ERP's own words and labelled as an
 * ERP value. Guessing that `8 H CLAIM SCHADENERS` means "damage compensation" would be this
 * page inventing business vocabulary, which decision 2 reserves for the business.
 *
 * P4 ADDS THE TWO CANVAS COLUMNS P3 REFUSED TO DRAW - Booking review and Recourse. They were
 * left out on purpose while no table fed them: a column that can only ever read "Open" is not a
 * preview of a feature, it is a lie about one. Now they read real rows, and the absence of a row
 * is itself a state each of them names.
 *
 * THE REASON COLUMN NOW SHOWS THE APP'S REASON, WITH ITS PROVENANCE. Where the ERP carries
 * `GU_Category_BIG` the value is the ERP's and the row says so; where it does not, the value is
 * what a reviewer recorded, or an explicit "not recorded". One vocabulary across all eight
 * companies (decision 2) is what makes dbi, bin, dch and dit comparable at all - and the
 * provenance beside it is what keeps "the ERP said this" apart from "a colleague decided this".
 *
 * The label is still the ERP's own 20-character stub, because the catalogue's long texts are
 * NULL until Stefan writes them (open question 1). None is invented here.
 */

export type SortKey =
    'postingDate' | 'company' | 'documentNo' | 'customer' | 'amount' | 'reason'
    | 'reviewStatus' | 'recourseStatus';

interface Col {
    id: string;
    label: string;
    sort?: SortKey;
    align?: 'left' | 'right';
    width: number;
}

const WIDTH_KEY = 'credit-notes-col-widths';

const COLS: Col[] = [
    { id: 'flags', label: 'Flags', width: 230 },
    { id: 'company', label: 'Co.', sort: 'company', width: 70 },
    { id: 'documentNo', label: 'Credit note', sort: 'documentNo', width: 130 },
    { id: 'postingDate', label: 'Date', sort: 'postingDate', width: 100 },
    { id: 'customer', label: 'Customer', sort: 'customer', width: 260 },
    { id: 'amount', label: 'Amount EUR', sort: 'amount', align: 'right', width: 125 },
    { id: 'itemLines', label: 'Item lines', align: 'right', width: 85 },
    { id: 'reason', label: 'Reason', sort: 'reason', width: 200 },
    { id: 'recourse', label: 'Recourse', sort: 'recourseStatus', width: 120 },
    { id: 'review', label: 'Booking review', sort: 'reviewStatus', width: 130 },
    { id: 'links', label: 'Links', width: 110 },
];

interface Props {
    documents: CreditNoteDocument[];
    skipReasonLabels: Record<string, string>;
    /** The group vocabulary, from the server. The page never invents a label for a code. */
    reasonCatalog: CreditNoteReasonCatalogEntry[];
    /** Status labels straight from the CHECK constraints, so every stored value renders. */
    vocabularies: CreditNoteVocabularies;
    loading: boolean;
    sortBy: string;
    sortOrder: 'asc' | 'desc';
    onSort: (key: SortKey) => void;
    selectedKey: string | null;
    onSelect: (doc: CreditNoteDocument) => void;
    onCopy: (value: string) => void;
    hasFilters: boolean;
}

export function CreditNoteTable({
    documents, skipReasonLabels, reasonCatalog, vocabularies, loading, sortBy, sortOrder, onSort,
    selectedKey, onSelect, onCopy, hasFilters,
}: Props) {
    const reviewLabel = new Map(vocabularies.reviewStatuses.map(v => [v.code as string, v.label]));
    const recourseLabel = new Map(vocabularies.recourseStatuses.map(v => [v.code as string, v.label]));
    const { widthOf, totalWidthOf, resizingKey, startResize, resetWidth, shouldSuppressClick } =
        useResizableColumns(COLS.map(c => ({ key: c.id, width: c.width })), WIDTH_KEY);
    const totalWidth = totalWidthOf(COLS.map(c => c.id));

    const sort: SortState<SortKey> = {
        key: (COLS.find(c => c.sort === sortBy)?.sort ?? null),
        direction: sortOrder,
    };

    if (loading && documents.length === 0) {
        return (
            <div className="rounded-lg border border-gray-200 bg-white">
                <div className="flex h-48 items-center justify-center">
                    <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
                </div>
            </div>
        );
    }

    if (documents.length === 0) {
        return (
            <div className="rounded-lg border border-gray-200 bg-white">
                <div className="flex h-48 items-center justify-center px-6 text-center">
                    <p className="text-sm text-gray-500">
                        {hasFilters
                            ? 'No credit note matches these filters.'
                            : 'No credit notes in this period.'}
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="rounded-lg border border-gray-200 bg-white">
            {/* The bounded scroll container the sticky header sticks inside of. */}
            <div className="max-h-[70vh] overflow-auto">
                <table className="w-full table-fixed text-sm" style={{ minWidth: totalWidth }}>
                    <colgroup>
                        {COLS.map(c => <col key={c.id} style={{ width: widthOf(c.id) }} />)}
                    </colgroup>
                    <thead>
                        <tr>
                            {COLS.map(c => (c.sort ? (
                                <SortableHeader
                                    key={c.id}
                                    label={c.label}
                                    sortKey={c.sort}
                                    sort={sort}
                                    // A finished resize ends with a click inside the cell; without
                                    // swallowing exactly that one, every drag also re-sorts.
                                    onSort={(key) => { if (!shouldSuppressClick()) onSort(key); }}
                                    align={c.align === 'right' ? 'right' : 'left'}
                                    className="shadow-[inset_0_-1px_0_rgb(229,231,235)]"
                                    onResizeStart={(x) => startResize(c.id, x)}
                                    onResetWidth={() => resetWidth(c.id)}
                                    isResizing={resizingKey === c.id}
                                />
                            ) : (
                                <th
                                    key={c.id}
                                    className={`sticky top-0 z-10 relative bg-gray-50 px-3 py-2 text-xs font-medium text-gray-500 shadow-[inset_0_-1px_0_rgb(229,231,235)] ${
                                        c.align === 'right' ? 'text-right' : 'text-left'
                                    }`}
                                >
                                    <span className="truncate">{c.label}</span>
                                    <ColumnResizeHandle
                                        label={c.label || c.id}
                                        active={resizingKey === c.id}
                                        onStart={(x) => startResize(c.id, x)}
                                        onReset={() => resetWidth(c.id)}
                                    />
                                </th>
                            )))}
                        </tr>
                    </thead>
                    <tbody>
                        {documents.map(doc => {
                            const key = creditNoteKey(doc.companyCode, doc.documentNo);
                            const selected = key === selectedKey;
                            return (
                                <tr
                                    key={key}
                                    onClick={() => onSelect(doc)}
                                    className={`cursor-pointer border-b border-gray-100 ${
                                        selected ? 'bg-blue-50' : 'hover:bg-gray-50'
                                    }`}
                                >
                                    <td className="px-3 py-2">
                                        <CreditNoteFlags doc={doc} skipReasonLabels={skipReasonLabels} />
                                    </td>
                                    <td className="px-3 py-2 font-medium text-gray-600">{doc.companyCode}</td>
                                    <td className="truncate px-3 py-2 font-mono text-xs text-gray-900" title={doc.documentNo}>
                                        {doc.documentNo}
                                    </td>
                                    <td className="px-3 py-2 text-gray-700">{formatDate(doc.postingDate)}</td>
                                    <td
                                        className="truncate px-3 py-2 text-gray-700"
                                        title={`${doc.customerName ?? ''}${doc.customerNr ? ` (${doc.customerNr})` : ''}`}
                                    >
                                        {doc.customerName ?? <span className="text-gray-400">No customer on the header</span>}
                                    </td>
                                    <td className="px-3 py-2 text-right font-medium tabular-nums text-red-700">
                                        {formatCredited(doc.amountEur)}
                                        {/* No monthly rate for that currency and month: the EUR
                                            figure is missing, not zero, and must not read as a
                                            number anyone can add up. */}
                                        {!doc.amountEurIsExact && (
                                            <span
                                                className="ml-1 text-[10px] font-normal text-amber-600"
                                                title="No monthly exchange rate for this currency and month — the document-currency amount is in the drawer."
                                            >
                                                ≈
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-3 py-2 text-right tabular-nums text-gray-600">
                                        {doc.itemLineCount === 0
                                            ? <span className="text-gray-400">none</span>
                                            : formatCount(doc.itemLineCount)}
                                    </td>
                                    <ReasonCell doc={doc} catalog={reasonCatalog} />
                                    <RecourseCell doc={doc} labels={recourseLabel} />
                                    <ReviewCell doc={doc} labels={reviewLabel} />
                                    <td className="px-3 py-2">
                                        <CreditNoteLinks doc={doc} onCopy={onCopy} />
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

/**
 * The reason, with where it came from.
 *
 * "not recorded" IS AMBER ONLY WHERE SOMEBODY COULD ACT ON IT. A dch credit note without a reason
 * is work waiting to be done; the same emptiness on a document whose ERP carries its own code is
 * not a task at all. Colouring both the same would fill the column with amber a reviewer learns
 * to scroll past.
 */
function ReasonCell({ doc, catalog }: { doc: CreditNoteDocument; catalog: CreditNoteReasonCatalogEntry[] }) {
    const reason = doc.app.reason;
    if (!reason) {
        return (
            <td className="truncate px-3 py-2" title="No reason recorded - neither in the ERP nor in the app.">
                <span className={doc.app.reasonEditable ? 'text-amber-700' : 'text-gray-400'}>not recorded</span>
            </td>
        );
    }
    const entry = catalog.find(c => c.reasonCode === reason.reasonCode);
    const label = reasonLabel(entry, reason.reasonCode);
    const origin = reason.source === 'ERP' ? 'from the ERP (GU_Category_BIG)' : 'recorded in the app';
    return (
        <td className="truncate px-3 py-2 text-gray-700" title={label + ' \u00b7 ' + origin}>
            {label}
            <span className="ml-1.5 text-[10px] uppercase tracking-wide text-gray-400">
                {reason.source === 'ERP' ? 'erp' : 'app'}
            </span>
        </td>
    );
}

/** No case at all is a state of its own - never folded into "Open". */
function RecourseCell({ doc, labels }: { doc: CreditNoteDocument; labels: Map<string, string> }) {
    const status = doc.app.recourseStatus;
    if (status === 'NONE') {
        return <td className="truncate px-3 py-2"><span className="text-gray-400">None</span></td>;
    }
    const overdue = doc.app.recourseOverdue;
    const settled = status === 'SETTLED';
    return (
        <td className="truncate px-3 py-2">
            <span
                className={`rounded px-1.5 py-0.5 text-[11px] font-medium ${
                    overdue ? 'bg-red-50 text-red-700'
                        : settled ? 'bg-green-50 text-green-700'
                            : 'bg-blue-50 text-blue-700'
                }`}
                title={overdue ? 'Past its follow-up date and still open' : undefined}
            >
                {labels.get(status) ?? status}
            </span>
        </td>
    );
}

function ReviewCell({ doc, labels }: { doc: CreditNoteDocument; labels: Map<string, string> }) {
    const { status } = doc.app.review;
    const tone = status === 'OK' ? 'text-green-700'
        : status === 'QUESTIONABLE' ? 'text-red-700'
            : status === 'OPEN' ? 'text-gray-500'
                : 'text-gray-700';
    return (
        <td className="truncate px-3 py-2">
            <span
                className={tone}
                title={doc.app.review.isDefault
                    ? 'Open because nobody has recorded a verdict yet'
                    : doc.app.review.changeReason ?? undefined}
            >
                {labels.get(status) ?? status}
            </span>
        </td>
    );
}
